import { useContext, useMemo } from 'react';
import { useParams } from 'next/navigation';
import { useInView } from 'react-intersection-observer';
import { lazyloadConfig } from '@/helpers/utils/brandButton';
import { CurrencyHelpers } from '@/helpers/utils/currencyHelpers';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import LearnMore from './learnMore';
import { YearPlan } from './yearPlan';
import ProtectionPlanIcon from '../../../../../../customercenter-ui/components/icons/pdpProtection';
import { ProductContext } from '../../contexts';
import { getAttribute } from '../../helpers/utils/productDataUtils';

interface Props {
  setSelectedWarrantySku: (value: string) => void;
  selectedWarrantySku?: string;
}
export const PdpProtectionPlan: React.FC<Props> = ({ setSelectedWarrantySku, selectedWarrantySku }) => {
  const { ref, inView } = useInView({
    rootMargin: lazyloadConfig.rootMargin,
    triggerOnce: true,
  });
  const { initialColor, initialSize } = useContext(ProductContext);
  const { department, classValue, subClassValue } = useMemo(
    () => ({
      department: initialSize ? getAttribute('department', initialSize) ?? undefined : undefined,
      classValue: initialSize ? getAttribute('class', initialSize) ?? undefined : undefined,
      subClassValue: initialSize ? getAttribute('subClass', initialSize) ?? undefined : undefined,
    }),
    [initialSize],
  );
  let { locale } = useParams();
  if (!locale) {
    locale = 'en';
  }
  return (
    <section className="rounded-sm border border-foggyGray bg-lightSilver p-4 text-base leading-5 md:p-5" ref={ref}>
      {inView && (
        <>
          <section className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <section className="flex items-center gap-2">
              <ProtectionPlanIcon className="size-7 shrink-0" />
              <h2 className="text-base font-bold leading-6 md:text-lg">
                {mapLocaleToMeaningfulFormat().protectYourPurchase}
              </h2>
            </section>
            <section className="text-sm">
              <LearnMore department={department} classValue={classValue} subClassValue={subClassValue} />
            </section>
          </section>
          <ul className="mt-3 flex w-full flex-col gap-3 md:px-9">
            {initialColor?.warrantyProducts ? (
              initialColor?.warrantyProducts?.map((dataObj: any, indx: number) => {
                return (
                  <YearPlan
                    key={indx}
                    year={dataObj?.displayName}
                    id={'protection_plan_' + indx}
                    price={
                      dataObj?.listPrice ? CurrencyHelpers.formatForCurrency(dataObj?.listPrice, false, locale) : null
                    }
                    ppm={
                      dataObj?.pricePerMonth
                        ? mapLocaleToMeaningfulFormat([
                            CurrencyHelpers.formatForCurrency(dataObj?.pricePerMonth, false, locale),
                          ]).onlyMorePricePerMonth
                        : ''
                    }
                    required={true}
                    warrantyVariant={dataObj?.warrantyVariant}
                    selectedWarrantySku={selectedWarrantySku}
                    className="flex cursor-pointer items-center gap-4 rounded-md bg-white shadow-sm"
                    setSelectedWarrantySku={setSelectedWarrantySku}
                  />
                );
              })
            ) : (
              <></>
            )}
          </ul>
        </>
      )}
    </section>
  );
};
